import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, ChevronLeft, ChevronRight } from "lucide-react";

export default function QuizTake() {
    const { quizId } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [attemptId, setAttemptId] = useState(null);
    const [answers, setAnswers] = useState({}); // qid -> [optionIds]
    const [idx, setIdx] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const submitted = useRef(false);

    useEffect(() => {
        (async () => {
            const { data: q } = await api.get(`/quizzes/${quizId}`);
            // randomize question order
            if (q.randomize) q.questions.sort(() => Math.random() - 0.5);
            setQuiz(q);
            setTimeLeft(q.time_limit_seconds);
            const { data: att } = await api.post(`/quizzes/${quizId}/attempts`);
            setAttemptId(att.id);
        })();
    }, [quizId]);

    useEffect(() => {
        if (timeLeft <= 0 || !quiz) return;
        const t = setInterval(() => setTimeLeft((s) => s - 1), 1000);
        return () => clearInterval(t);
    }, [quiz, timeLeft]);

    useEffect(() => {
        if (quiz && timeLeft === 0 && !submitted.current) submit();
    }, [timeLeft, quiz]); // eslint-disable-line

    const toggle = (qid, oid, type) => {
        setAnswers(prev => {
            const sel = new Set(prev[qid] || []);
            if (type === "MULTI") {
                if (sel.has(oid)) sel.delete(oid); else sel.add(oid);
            } else {
                sel.clear();
                sel.add(oid);
            }
            return { ...prev, [qid]: Array.from(sel) };
        });
    };

    const submit = async () => {
        if (submitted.current) return;
        submitted.current = true;
        const payload = { answers: quiz.questions.map(q => ({ question_id: q.id, selected_option_ids: answers[q.id] || [] })) };
        try {
            const { data } = await api.post(`/attempts/${attemptId}/submit`, payload);
            toast.success(data.passed ? `Passed with ${data.percentage}%` : `Scored ${data.percentage}%`);
            navigate(`/student/quizzes/${quizId}/result/${attemptId}`);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    if (!quiz) return <Skeleton className="h-96" />;

    const q = quiz.questions[idx];
    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="quiz-take-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Quiz</div>
                    <h1 className="font-display text-2xl lg:text-3xl mt-1">{quiz.title}</h1>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 ${timeLeft < 60 ? "border-[#E11D48] text-[#E11D48]" : "border-[#726AF0] text-[#726AF0]"}`} data-testid="quiz-timer">
                    <Clock className="h-4 w-4" />
                    <span className="font-display text-lg">{fmt(timeLeft)}</span>
                </div>
            </div>

            <div className="flex gap-1.5">
                {quiz.questions.map((_, i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i < idx ? "bg-[#726AF0]" : i === idx ? "bg-brand-gradient" : "bg-muted"}`} />
                ))}
            </div>

            <Card className="mx-card">
                <CardContent className="p-8">
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Question {idx + 1} of {quiz.questions.length}</div>
                    <h2 className="font-bold text-lg mt-2">{q.text}</h2>
                    <div className="text-xs text-muted-foreground mt-1">{q.type === "MULTI" ? "Select all that apply" : "Select one"}</div>
                    <div className="mt-6 space-y-2.5">
                        {q.options.map(o => {
                            const selected = (answers[q.id] || []).includes(o.id);
                            return (
                                <button
                                    key={o.id}
                                    onClick={() => toggle(q.id, o.id, q.type)}
                                    data-testid={`quiz-option-${o.id}`}
                                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected ? "border-[#726AF0] bg-[#F0EFFF] dark:bg-[#1C1A2E]" : "border-border hover:border-[#726AF0]/50"}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`h-5 w-5 ${q.type === "MULTI" ? "rounded-md" : "rounded-full"} border-2 ${selected ? "border-[#726AF0] bg-[#726AF0]" : "border-border"} flex items-center justify-center`}>
                                            {selected && <div className="h-2 w-2 bg-white rounded-sm" />}
                                        </div>
                                        <span className="font-medium">{o.text}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            <div className="flex justify-between gap-3">
                <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="rounded-xl" data-testid="quiz-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Previous</Button>
                {idx < quiz.questions.length - 1 ? (
                    <Button onClick={() => setIdx(idx + 1)} className="mx-btn-primary" data-testid="quiz-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
                ) : (
                    <Button onClick={submit} className="mx-btn-primary" data-testid="quiz-submit">Submit quiz</Button>
                )}
            </div>
        </div>
    );
}
